import { useState } from 'react';
import styled from 'styled-components';

import Badge from 'components/shared/Badge';
import Button from 'components/shared/Button';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Workflow, { WorkflowAction, WorkflowGroupOptions } from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import CircleCheckIcon from 'svg/CircleCheckIcon';
import CircleXIconOutlined from 'svg/CircleXIconOutlined';

import { completionAttribution, sortedGroupsByName } from './FanoutPanel';
import { Pill, Tone } from './StatusBadge';

// The always-available Referral Outcome (OLIO-2611): won or lost, and on won, which
// facility. The step sits quietly at Pending on the API side (never demands attention),
// but this panel is interactive the whole time — a coordinator records Lost the moment
// the patient chooses elsewhere. The winner list shows every targeted building, alpha
// ordered, each with its current facility-decision pill — informing the pick rather
// than filtering it (deliberately permissive; the API doesn't enforce it either).
interface Props {
  action: WorkflowAction;
  workflow: Workflow;
  onComplete: (actionId: string, outputs: Record<string, string>) => void;
  isCompleting: boolean;
}

export default function ReferralOutcomePanel({ action, workflow, onComplete, isCompleting }: Props) {
  const [outcome, setOutcome] = useState<'won' | 'lost' | null>(null);
  const [facilityId, setFacilityId] = useState('');

  const facilities = sortedGroupsByName(workflow.groups);

  const decisionDisplay = (groupId: string): { label: string; tone: Tone } => {
    const value = workflow.valueFor('facility_determination', groupId)?.value;
    if (value === 'accepted') return { label: 'Accepted', tone: 'success' };
    if (value === 'denied') return { label: 'Denied', tone: 'danger' };
    return { label: 'Pending', tone: 'neutral' };
  };

  const canSave = !isCompleting && (outcome === 'lost' || (outcome === 'won' && !!facilityId));

  const save = () => {
    if (!outcome) return;
    onComplete(
      action.id,
      outcome === 'won' ? { final_outcome: 'won', selected_facility_id: facilityId } : { final_outcome: 'lost' }
    );
  };

  return (
    <Wrap data-testid='referral-outcome-panel'>
      <BodySmall>What is the final outcome status for this referral?</BodySmall>
      <Choices>
        <ChoiceButton
          type='button'
          $selected={outcome === 'won'}
          onClick={() => setOutcome('won')}
          data-testid='outcome-won'>
          <IconWrap $color={colors.green100}>
            <CircleCheckIcon width={28} height={28} />
          </IconWrap>
          Won Referral
        </ChoiceButton>
        <ChoiceButton
          type='button'
          $selected={outcome === 'lost'}
          onClick={() => setOutcome('lost')}
          data-testid='outcome-lost'>
          <IconWrap $color={colors.red100}>
            <CircleXIconOutlined width={28} height={28} />
          </IconWrap>
          Lost Referral
        </ChoiceButton>
      </Choices>

      {outcome === 'won' && (
        <FacilityField>
          <BodySmall>Select which facility the patient selected</BodySmall>
          <Select<WorkflowGroupOptions>
            options={facilities}
            value={facilities.find((group) => group.id === facilityId) ?? null}
            onChange={(group) => setFacilityId(group?.id ?? '')}
            getOptionValue={(group) => group.id}
            getOptionLabel={(group) => group.name}
            formatOptionLabel={(group, meta) => {
              if (meta?.context === 'value') return group.name;
              const display = decisionDisplay(group.id);
              return (
                <OptionRow>
                  {group.name}
                  <Pill $tone={display.tone}>{display.label}</Pill>
                </OptionRow>
              );
            }}
            placeholder={workflow.groups[0]?.groupType?.displayName ?? 'Select a facility'}
            dataTestId='outcome-facility'
            // The card clips overflow, so the open menu must portal out of it; near the
            // bottom of the page it flips upward instead of growing the document.
            menuPortalTarget={document.body}
            menuPlacement='auto'
            maxMenuHeight={240}
          />
        </FacilityField>
      )}

      <ConfirmRow>
        <Button disabled={!canSave} onClick={save} data-testid='outcome-save'>
          Confirm
        </Button>
      </ConfirmRow>
    </Wrap>
  );
}

// The completed state (Figma): one row per targeted building with its verdict chip —
// the winning facility (if won) reads Referral Won, every other building Referral Lost —
// plus who recorded the outcome and when.
export function ReferralOutcomeResult({ action, workflow }: { action: WorkflowAction; workflow: Workflow }) {
  const outcome = workflow.valueFor('final_outcome')?.value;
  const winnerId = workflow.valueFor('selected_facility_id')?.value;
  const updated = completionAttribution(action);

  // Runs recorded under an older vocabulary (e.g. final_outcome 'accepted' with no
  // winning facility) can't honestly render per-building Won/Lost rows — show the raw
  // recorded outcome instead of badging every building 'Referral Lost'.
  if (outcome !== 'lost' && !(outcome === 'won' && winnerId)) {
    return (
      <ResultRows data-testid='referral-outcome-result'>
        <ResultRow>
          <ResultName>Recorded outcome: {typeof outcome === 'string' ? outcome : 'unknown'}</ResultName>
          {updated && <ResultUpdated>{updated}</ResultUpdated>}
        </ResultRow>
      </ResultRows>
    );
  }

  return (
    <ResultRows data-testid='referral-outcome-result'>
      {sortedGroupsByName(workflow.groups).map((group) => {
        const won = group.id === winnerId;
        return (
          <ResultRow key={group.id} data-testid='outcome-row' data-group-id={group.id}>
            <ResultHeader>
              <ResultName>{group.name}</ResultName>
              <Badge color={won ? 'green' : 'red'}>{won ? 'Referral Won' : 'Referral Lost'}</Badge>
            </ResultHeader>
            {updated && <ResultUpdated>{updated}</ResultUpdated>}
          </ResultRow>
        );
      })}
    </ResultRows>
  );
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Choices = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const ChoiceButton = styled.button<{ $selected: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 28px 24px;
  border-radius: 8px;
  border: 1px solid ${({ $selected }) => ($selected ? colors.blue100 : colors.black10)};
  background: ${({ $selected }) => ($selected ? colors.blue10 : colors.white)};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
`;

// Both icons draw with currentColor (the check via its fill default, the X via stroke).
const IconWrap = styled.span<{ $color: string }>`
  display: inline-flex;
  color: ${({ $color }) => $color};
`;

const ResultRows = styled.div`
  display: flex;
  flex-direction: column;
`;

const ResultRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 0;

  & + & {
    border-top: 1px solid ${colors.black5};
  }
`;

const ResultHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ResultName = styled(BodySmallBold)`
  font-size: 14px;
`;

const ResultUpdated = styled(BodySmall)`
  color: ${colors.black50};
`;

const OptionRow = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const ConfirmRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const FacilityField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 420px;
`;
